import { z } from 'zod'
import { isoDateTime, uuid } from './common.js'
import { recordableConsentPurpose } from './consent.js'

/**
 * Health, dietary and accessibility notes — the data `consents` exists to
 * protect. Backed by `participant_sensitive_notes` (migrations 0010/0011): a
 * table of its own, with no grant to `itin_share` of any kind, precisely so
 * that the anonymous role's column-listed SELECT on `participants` can never
 * accidentally extend to it.
 *
 * One row per (participant, purpose) — the database enforces this with
 * `unique(participant_id, purpose)`, so writing again for the same purpose
 * updates the existing note rather than accumulating a second one.
 */
export const upsertSensitiveNoteRequest = z.object({
  body: z.string().trim().min(1).max(4000),
})
export type UpsertSensitiveNoteRequest = z.infer<typeof upsertSensitiveNoteRequest>

export const sensitiveNote = z.object({
  id: uuid,
  tripId: uuid,
  participantId: uuid,
  purpose: recordableConsentPurpose,
  /**
   * **Null whenever there is no active, not-withdrawn consent for this
   * participant and purpose.** The row is never deleted on withdrawal — same
   * LGPD Art. 16 reasoning as `consents.statement` — but nothing that reads
   * through this contract can see the content once consent is gone. Checked
   * fresh on every read, in `sensitiveNotes.service.ts`, not cached onto the
   * row: a withdrawal takes effect on the very next request.
   */
  body: z.string().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type SensitiveNote = z.infer<typeof sensitiveNote>
